/**
 * AuthRedirectHandler Component
 *
 * Handles post-authentication role-based redirection.
 * After a user successfully authenticates, this component:
 * 1. Waits for authentication to complete
 * 2. Reads the user's roles from the ID token
 * 3. Redirects to the appropriate downstream application
 *
 * Redirect logic:
 * - Admin role → https://admin.hirenow.com
 * - User role (or default) → https://user.hirenow.com
 */

import { useEffect, useState } from 'react';
import { useAuth, getRedirectUrlForRoles } from '../auth';
import { Loading } from '../components';

/**
 * Redirect status for tracking the redirection process
 */
type RedirectStatus =
  | 'checking' // Checking authentication status
  | 'fetching-roles' // Fetching user roles
  | 'redirecting' // About to redirect
  | 'error'; // An error occurred

/**
 * AuthRedirectHandler Component
 *
 * Automatically redirects authenticated users to their appropriate
 * downstream application based on their roles.
 */
export function AuthRedirectHandler() {
  const { isAuthenticated, isLoading, roles, user, error } = useAuth();
  const [status, setStatus] = useState<RedirectStatus>('checking');
  const [redirectUrl, setRedirectUrl] = useState<string | null>(null);

  useEffect(() => {
    // Still loading authentication state
    if (isLoading) {
      setStatus('checking');
      return;
    }

    // Not authenticated - this shouldn't happen if routing is correct
    if (!isAuthenticated) {
      setStatus('checking');
      return;
    }

    // Authentication error
    if (error) {
      setStatus('error');
      return;
    }

    // Determine redirect URL based on roles
    setStatus('fetching-roles');

    const targetUrl = getRedirectUrlForRoles(roles);
    setRedirectUrl(targetUrl);
    setStatus('redirecting');

    // Log the redirect for debugging
    const isAdmin = roles.some((role) => role.toLowerCase() === 'admin');
    console.info(
      `[AuthRedirectHandler] Redirecting ${user?.email ?? 'user'} to ${targetUrl}`,
      {
        roles,
        isAdmin,
      }
    );

    // Perform the redirect
    // Using replace to prevent back-button returning to auth page
    window.location.replace(targetUrl);
  }, [isAuthenticated, isLoading, roles, user, error]);

  // Render appropriate UI based on status
  switch (status) {
    case 'checking':
      return (
        <Loading fullScreen message="Verifying authentication..." />
      );

    case 'fetching-roles':
      return (
        <Loading fullScreen message="Loading your profile..." />
      );

    case 'redirecting':
      return (
        <div className="redirect-container">
          <Loading message="Redirecting you..." />
          <p className="redirect-message">
            Taking you to{' '}
            <a href={redirectUrl ?? '#'} className="redirect-link">
              {redirectUrl}
            </a>
          </p>
          <p className="redirect-fallback">
            If you are not redirected automatically,{' '}
            <a href={redirectUrl ?? '#'} className="redirect-link">
              click here
            </a>
            .
          </p>
        </div>
      );

    case 'error':
      return (
        <div className="redirect-error">
          <h2>Authentication Error</h2>
          <p>{error?.message ?? 'An unexpected error occurred'}</p>
          <a href="/" className="error-back-link">
            Return to login
          </a>
        </div>
      );

    default:
      return <Loading fullScreen />;
  }
}

/**
 * ProtectedRoute Component
 *
 * A wrapper component that ensures the user is authenticated
 * before rendering children. If not authenticated, redirects to login.
 */
interface ProtectedRouteProps {
  children: React.ReactNode;
  /** Required roles to access this route (optional) */
  requiredRoles?: string[];
  /** Fallback component to show while loading */
  fallback?: React.ReactNode;
}

export function ProtectedRoute({
  children,
  requiredRoles,
  fallback,
}: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, roles } = useAuth();

  // Show loading state
  if (isLoading) {
    return <>{fallback ?? <Loading fullScreen message="Loading..." />}</>;
  }

  // Not authenticated - redirect to login
  if (!isAuthenticated) {
    // Use window.location for full page navigation
    window.location.replace('/');
    return <Loading fullScreen message="Redirecting to login..." />;
  }

  // Check required roles if specified
  if (requiredRoles && requiredRoles.length > 0) {
    const hasRequiredRole = requiredRoles.some((required) =>
      roles.some((role) => role.toLowerCase() === required.toLowerCase())
    );

    if (!hasRequiredRole) {
      return (
        <div className="access-denied">
          <h2>Access Denied</h2>
          <p>You do not have permission to access this page.</p>
          <p>Required roles: {requiredRoles.join(', ')}</p>
        </div>
      );
    }
  }

  return <>{children}</>;
}
